import { useHandler } from "@redotech/react-util/hook";
import { AutocheckRescuePanelActiveSplit } from "@redotech/redo-conversion-core-common/conversion/conversion-autocheck";
import { RESCUE_PANEL_TREATMENT_KEY } from "@redotech/redo-shopify-common/cart";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { RESCUE_PANEL_TREATMENT_ID_LOCAL_STORAGE_KEY } from "@redotech/redo-web/utils/shared-conf";
import { getShopifySessionId } from "@redotech/redo-web/utils/shopify-session";
import { recordRescuePanelCtaClicked } from "@redotech/shopify-sdk/rpc/schema/record-rescue-panel-cta-clicked";
import { recordRescuePanelTriggered } from "@redotech/shopify-sdk/rpc/schema/record-rescue-panel-triggered";
import { useContext, useEffect, useMemo } from "react";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { StorefrontClient } from "../../client/storefront";

const RESCUE_PANEL_SPLIT_TREATMENT_DURATION_HOURS = 1;
const RESCUE_PANEL_TRIGGERED_METRIC_EXPIRY_HOURS = 24;
const RESCUE_PANEL_CTA_CLICKED_METRIC_EXPIRY_HOURS = 24;

type RescuePanelSplit =
  | {
      splitActive: true;
      panelAllowedForTreatment: boolean;
      dropoffPercentageTrigger?: number;
      secondsInColdTrigger?: number;
      spotlightId?: string;
      onShouldShowPanel: () => Promise<void> | null;
      onPanelCtaClicked: () => Promise<void> | null;
    }
  | { splitActive: false };

export function useRescuePanelSplit({
  autocheckRescuePanelSplit,
  storefrontClient,
}: {
  autocheckRescuePanelSplit: AutocheckRescuePanelActiveSplit | null;
  storefrontClient: StorefrontClient;
}): RescuePanelSplit {
  const shopifyRpcClient = useContext(RedoShopifyRpcClientContext);

  const selectedTreatment = useMemo(() => {
    if (!autocheckRescuePanelSplit) {
      return undefined;
    }

    return autocheckRescuePanelSplit.treatments.find(
      (treatment) =>
        treatment._id === autocheckRescuePanelSplit.selectedTreatmentId,
    );
  }, [autocheckRescuePanelSplit]);

  useEffect(() => {
    if (selectedTreatment) {
      setLocalStorageWithExpiry(
        RESCUE_PANEL_TREATMENT_ID_LOCAL_STORAGE_KEY,
        selectedTreatment._id,
        RESCUE_PANEL_SPLIT_TREATMENT_DURATION_HOURS,
      );
    }
  }, [selectedTreatment]);

  const onShouldShowPanel = useHandler(async () => {
    if (!selectedTreatment) {
      return;
    }

    const sessionId = getShopifySessionId();
    if (!sessionId) {
      console.warn(
        "Session ID not available for recording rescue panel trigger",
      );
    } else if (shopifyRpcClient) {
      const localStorageKey = constructTriggeredLocalStorageKey({
        treatmentId: selectedTreatment._id,
        sessionId,
      });

      const hasAlreadyRecorded = getLocalStorageWithExpiry(localStorageKey);

      if (!hasAlreadyRecorded) {
        setLocalStorageWithExpiry(
          localStorageKey,
          "true",
          RESCUE_PANEL_TRIGGERED_METRIC_EXPIRY_HOURS,
        );

        try {
          await shopifyRpcClient(recordRescuePanelTriggered, {
            treatmentId: selectedTreatment._id,
          });
        } catch (err) {
          console.error("Failed to record rescue panel triggered metric", err);
        }
      }
    }

    // Set the treatment ID attribute on the cart
    await storefrontClient.updateCartAttribute({
      attributes: [
        { key: RESCUE_PANEL_TREATMENT_KEY, value: selectedTreatment._id },
      ],
    });
  });

  const onPanelCtaClicked = useHandler(async () => {
    if (!selectedTreatment) {
      return;
    }

    const sessionId = getShopifySessionId();
    if (!sessionId) {
      console.warn(
        "Session ID not available for recording rescue panel CTA click",
      );
    } else if (shopifyRpcClient) {
      const localStorageKey = constructCtaClickedLocalStorageKey({
        treatmentId: selectedTreatment._id,
        sessionId,
      });

      const hasAlreadyRecorded = getLocalStorageWithExpiry(localStorageKey);

      if (!hasAlreadyRecorded) {
        setLocalStorageWithExpiry(
          localStorageKey,
          "true",
          RESCUE_PANEL_CTA_CLICKED_METRIC_EXPIRY_HOURS,
        );

        try {
          await shopifyRpcClient(recordRescuePanelCtaClicked, {
            treatmentId: selectedTreatment._id,
          });
        } catch (err) {
          console.error(
            "Failed to record rescue panel CTA clicked metric",
            err,
          );
        }
      }
    }
  });

  const rescuePanelSplit: RescuePanelSplit = useMemo(() => {
    if (!selectedTreatment) {
      return { splitActive: false };
    }

    return {
      splitActive: true,
      panelAllowedForTreatment: selectedTreatment.rescuePanelConfig.enabled,
      dropoffPercentageTrigger:
        selectedTreatment.rescuePanelConfig.dropoffPercentageTrigger,
      secondsInColdTrigger:
        selectedTreatment.rescuePanelConfig.secondsInColdTrigger,
      spotlightId: selectedTreatment.rescuePanelConfig.spotlightId,
      onShouldShowPanel,
      onPanelCtaClicked,
    };
  }, [selectedTreatment, onShouldShowPanel, onPanelCtaClicked]);

  return rescuePanelSplit;
}

const TRIGGERED_METRIC_LOCAL_STORAGE_KEY_PREFIX = "rrp-triggered-";
const CTA_CLICKED_METRIC_LOCAL_STORAGE_KEY_PREFIX = "rrp-cta-clicked-";

function constructTriggeredLocalStorageKey({
  treatmentId,
  sessionId,
}: {
  treatmentId: string;
  sessionId: string;
}): `${typeof TRIGGERED_METRIC_LOCAL_STORAGE_KEY_PREFIX}${string}-${string}` {
  return `${TRIGGERED_METRIC_LOCAL_STORAGE_KEY_PREFIX}${treatmentId}-${sessionId}`;
}

function constructCtaClickedLocalStorageKey({
  treatmentId,
  sessionId,
}: {
  treatmentId: string;
  sessionId: string;
}): `${typeof CTA_CLICKED_METRIC_LOCAL_STORAGE_KEY_PREFIX}${string}-${string}` {
  return `${CTA_CLICKED_METRIC_LOCAL_STORAGE_KEY_PREFIX}${treatmentId}-${sessionId}`;
}
